import { FC, useEffect, useState } from "react";

import { UserPlus } from "lucide-react";
import { CompanyBody, Role, User } from "@vviinn/user-managment-client-ts";

import { useAppSelector } from "../../store";
import { useRequestHandlers } from "../../app/hooks/useRequestHandlers";

import { MandatoryCompanyId } from "../../company";
import {
  companyUserRoles,
  globalUserRoles,
  mapGeneralRoleTitleToUserRoleTitle,
} from "./helpers";
import { RoleTitle } from "./types";

import { RoleSelect } from "./RoleSelect";

import { handleUser } from "../handleUser";
import { NamedSelect } from "../../ui/layout/molecules/NamedSelect";
import { SlideOverWrapper } from "../../ui/layout/organisms/SlideOver/SlideOverWrapper/SlideOverWrapper";
import {
  SLIDEOVER_EDIT_ICON_ICON_STYLES,
  SLIDEOVER_EDIT_ICON_WRAPPER_STYLES,
} from "../../ui/layout/organisms/SlideOver/styles";
import { Closeable } from "../../ui/layout/organisms/SlideOver/SlideOverWrapper/types";

type AssignCompanyModalProps = Closeable & {
  companies: CompanyBody[];
  user: User;
};

const tabs = ["Global", "Company"];

type CompanyId = MandatoryCompanyId | CompanyBody["id"] | null;

export const AssignUserSlideOver: FC<
  React.PropsWithChildren<AssignCompanyModalProps>
> = ({ open, companies, onClose, user }) => {
  const [role, setRole] = useState({
    kind: "company",
    role: "editor",
  } as RoleTitle);
  const [companyId, setCompanyId] = useState<CompanyId>(null);
  const [userRoles, setUserRoles] = useState(globalUserRoles);
  const [filteredCompanies, setFilteredCompanies] = useState(companies);
  const currentCompanyId = useAppSelector((state) => state.company.company?.id);
  const [currentTabIndex, setCurrentTabIndex] = useState(0);

  const handlerParams = useRequestHandlers();
  const { handleAssignCompanyRole, handleAssignGlobalRole } =
    handleUser(handlerParams);

  useEffect(() => {
    setCompanyId(companies[0]?.id ?? null);
  }, [companies.length]);

  const assignCompanyRole = async (userId: User["uid"], role: Role) => {
    if (!companyId || !currentCompanyId) return false;
    return await handleAssignCompanyRole(
      userId,
      role,
      currentCompanyId,
      companyId,
    );
  };

  const assignGlobalRole = async (userId: User["uid"], role: RoleTitle) => {
    return await handleAssignGlobalRole(userId, role);
  };

  const handleRoleAssignement = async ({
    userId,
    role,
  }: {
    userId: User["uid"];
    role: RoleTitle;
  }) => {
    const result = await (role.kind === "company"
      ? assignCompanyRole(userId, role.role as Role)
      : assignGlobalRole(userId, role));
    return !!result;
  };

  useEffect(() => {
    const roles = userRoles.map((role) => {
      let disabled = false;
      user?.roles.forEach((userRole) => {
        if (userRole === mapGeneralRoleTitleToUserRoleTitle(role))
          disabled = true;
      });

      return { ...role, disabled };
    });
    setUserRoles(roles);
  }, [user?.roles]);

  useEffect(() => {
    const assignedCompanies = user?.assignedCompanies.map(({ id }) => id);
    setFilteredCompanies(
      companies.filter((company) => {
        if (company.id) {
          return !assignedCompanies.includes(company.id);
        }
      }),
    );
  }, [user?.assignedCompanies, companies]);

  return (
    <SlideOverWrapper
      open={open}
      buttonProps={{
        onClick: async () =>
          await handleRoleAssignement({ userId: user.uid, role }),
      }}
      title="Assign new role to user"
      tabProps={{
        tabs: tabs,
        tabGroupReference: "roles",
        setActiveTabIndex: setCurrentTabIndex,
        defaultIndex: 0,
      }}
      onClose={onClose}
      initConfig={() => setCurrentTabIndex(0)}
    >
      <div className={SLIDEOVER_EDIT_ICON_WRAPPER_STYLES}>
        <UserPlus className={SLIDEOVER_EDIT_ICON_ICON_STYLES} />
      </div>

      {currentTabIndex === 0 && (
        <RoleSelect onChange={setRole} roles={userRoles} />
      )}

      {currentTabIndex === 1 && companyId && (
        <section>
          <NamedSelect
            title="Company roles"
            options={filteredCompanies.map(({ id, name }) => ({
              title: name ?? "",
              value: id,
            }))}
            capitalize={false}
            value={companyId}
            onChange={(target) => setCompanyId(target.value)}
            styleVariant="detailPage"
          />
          <RoleSelect onChange={setRole} roles={companyUserRoles} />
        </section>
      )}
    </SlideOverWrapper>
  );
};
